import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Group, Modal, NumberInput, SegmentedControl, Stack, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

interface BulkPriceDialogProps {
  readonly opened: boolean;
  /** Охват изменения — показываем явно (товар · базис), защита от случайностей. */
  readonly goods: string;
  readonly basisTitle: string;
  /** Максимально допустимое уменьшение, ₽/т (чтобы цены не ушли ниже минимума). 0 — уменьшать нельзя. */
  readonly maxDiscount: number;
  readonly loading?: boolean;
  /** Знаковая дельта: + увеличение, − уменьшение. */
  readonly onApply: (delta: number) => void;
  readonly onClose: () => void;
}

const MAX_VALUE = 999_999;

// Массовый сдвиг цен прайс-листа (как на старом портале): знак + сумма в ₽/т, применяется ко ВСЕМ
// строкам качества и обоим типам предоплаты группы. Охват показан явно.
export default function BulkPriceDialog({
  opened,
  goods,
  basisTitle,
  maxDiscount,
  loading,
  onApply,
  onClose,
}: BulkPriceDialogProps) {
  const { t } = useTranslation();
  const [sign, setSign] = useState<'+' | '-'>('+');
  const [value, setValue] = useState<number | string>('');

  useEffect(() => {
    if (opened) {
      setSign('+');
      setValue('');
    }
  }, [opened]);

  const numeric = typeof value === 'number' ? value : Number(value);
  const maxForSign = sign === '-' ? Math.max(0, maxDiscount) : MAX_VALUE;
  const valid = Number.isFinite(numeric) && numeric >= 1 && numeric <= maxForSign;
  const decreaseBlocked = sign === '-' && maxDiscount < 1;

  return (
    <Modal opened={opened} onClose={onClose} title={t('app.buy.bulk.title')} centered>
      <Stack gap="md">
        <Alert color="wheat" variant="light" icon={<IconInfoCircle size={18} />}>
          {t('app.buy.bulk.scope', { goods, basis: basisTitle })}
        </Alert>

        <SegmentedControl
          fullWidth
          value={sign}
          onChange={(next) => setSign(next as '+' | '-')}
          data={[
            { value: '+', label: t('app.buy.bulk.increase') },
            { value: '-', label: t('app.buy.bulk.decrease') },
          ]}
        />

        {decreaseBlocked ? (
          <Text size="sm" c="red.7">
            {t('app.buy.bulk.cantDecrease')}
          </Text>
        ) : (
          <NumberInput
            label={t('app.buy.bulk.amount')}
            description={sign === '-' ? t('app.buy.bulk.maxDiscount', { max: maxDiscount }) : undefined}
            min={1}
            max={maxForSign}
            allowNegative={false}
            allowDecimal={false}
            thousandSeparator=" "
            value={value}
            onChange={setValue}
          />
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => onApply(sign === '+' ? numeric : -numeric)}
            loading={loading}
            disabled={!valid || decreaseBlocked}
          >
            {t('app.buy.bulk.apply')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
